import dayjs from 'dayjs';

import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { isLoggedIn } from 'util/authHelpers';
import { hideModal, showModal } from 'actions/ModalActions';
import { trackModalViewed, trackModalClicked } from 'util/trackingHelper';
import { getUserWeddingDate } from 'selectors/user';

// zola-helpers
import ApiService from '@zola-helpers/client/dist/es/http/api';
import NAV_DATA from '@zola-helpers/client/dist/es/constants/navConstants';
import { getLocalStorage, setLocalStorage } from '@zola-helpers/client/dist/es/util/storage';
import { BUSINESS_UNIT } from '@zola-helpers/client/dist/es/tracking/onboard/onboardTrackingConstants';

// types
import type { RootState } from 'reducers/index';
import type { WUserSettingView } from '@zola/svc-web-api-ts-client';

// types are not complete, only fields that are needed are included
export interface ConnectedInquiry {
  uuid: string;
  storefront: {
    vendorName: string;
    taxonomyNode: {
      key: string;
    };
  };
}

const {
  EVENTS: { MODAL_TYPE_BOOKING_STATUS_REMINDER },
} = NAV_DATA;

export const BOOKING_STATUS_REMINDER_STORAGE_KEY = 'marketplace.bookingStatusReminderModal';
const minDaysModalViewed = 7; // minimum number of days to wait before modal reappears
const BOOKING_STATUS_REMINDER_MODAL_TYPE = 'BOOKING_STATUS_REMINDER';

// user settings key format: marketplace:inquiry:{uuid}:booked_reminder_dismissed
const minDaysInquiryConnected = 28; // minimum number of days that the inquiry has been in connected status
const minDaysInquiryViewed = 7; // minimum number of days that a specific inquiry has been viewed

/**
 * user settings are used to determine if an inquiry was previously viewed
 * the format for the key is marketplace:inquiry:{uuid}:booked_reminder_dismissed
 *
 * Please do not change the prefix and suffix!
 */
const KEY_PREFIX = 'marketplace:inquiry:';
const KEY_SUFFIX = ':booked_reminder_dismissed';
const getInquiryUserSettingsKey = (uuid: string) => `${KEY_PREFIX}${uuid}${KEY_SUFFIX}`;

const defaultTrackingPayload = {
  business_unit: BUSINESS_UNIT.MARKETPLACE,
  modal_type: BOOKING_STATUS_REMINDER_MODAL_TYPE,
};

const setUserSettings = (settingsKey: string) =>
  ApiService.post(`/website-nav/web-api/v1/userSettings/set`, { key: settingsKey });

const getUserSettings = (keysList: string[]): Promise<Record<string, WUserSettingView>> =>
  ApiService.post(`/website-nav/web-api/v1/userSettings/getList`, {
    keys: keysList,
  });

const getConnectedInquiriesList = (objectId: string): Promise<ConnectedInquiry[]> =>
  ApiService.get(
    `/web-marketplace-api/v1/inquiry/user/${objectId}/list-connected?for_days=${minDaysInquiryConnected}`
  );

/**
 * filter inquiries based on the user's settings
 */
const getFilteredInquiries = (userObjectId: string) =>
  getConnectedInquiriesList(userObjectId).then((inquiries) => {
    if (!inquiries.length) {
      return [];
    }

    const keysList = inquiries.map((inquiry) => getInquiryUserSettingsKey(inquiry.uuid));
    if (keysList.length > 0) {
      return getUserSettings(keysList).then((settings) => {
        return inquiries.filter((inquiry) => {
          const key = getInquiryUserSettingsKey(inquiry.uuid);
          if (key in settings && settings[key].updatedAt !== null) {
            return dayjs().diff(dayjs(settings[key].updatedAt), 'days') > minDaysInquiryViewed;
          }
          return true;
        });
      });
    }

    return [];
  });

/**
 *
 * This function will check if the modal should appear
 * based on path, last modal viewed, and any overrides
 */
const shouldShowReminder = (): boolean => {
  /**
   * modal should not appear if the user has seen it within N days using the same browser
   * on logout, the localStorage key is removed
   *
   * if localStorage is empty, assume that the modal never appeared before
   */
  const storedDate = getLocalStorage(BOOKING_STATUS_REMINDER_STORAGE_KEY);
  if (storedDate && dayjs().diff(dayjs(storedDate), 'days') <= minDaysModalViewed) {
    return false;
  }

  /**
   * modal should only appear in these pages:
   * Home page: /wedding/manage
   * Your Wedding page: /wedding/dashboard
   * any marketplace pages: inspiration/* and wedding-vendors/*
   */
  const { pathname } = window.location; // current url without the domain
  const acceptedPaths = /^\/(wedding\/(manage|dashboard)|(inspiration|wedding-vendors)($|\/.*?))/;
  if (acceptedPaths.test(pathname) === false) {
    return false;
  }

  return true;
};

let requestedInquiries = false;

const BookingStatusReminderContainer = (): null => {
  const dispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const weddingDate = useSelector((state: RootState) => getUserWeddingDate(state));

  /**
   * the modal should not appear if the current date is after the wedding date
   */
  useEffect(() => {
    if (
      shouldShowReminder() &&
      user.hasIdentified &&
      isLoggedIn(user.userContext) &&
      user.userContext.object_id &&
      user.userContext.marketplace_account?.id &&
      (weddingDate === undefined || weddingDate === null || dayjs(weddingDate) >= dayjs()) &&
      requestedInquiries === false
    ) {
      requestedInquiries = true;
      getFilteredInquiries(user.userContext.object_id)
        .then((inquiries) => {
          if (inquiries && inquiries.length > 0) {
            setLocalStorage(BOOKING_STATUS_REMINDER_STORAGE_KEY, new Date().toISOString());

            dispatch(
              showModal(
                MODAL_TYPE_BOOKING_STATUS_REMINDER,
                {
                  // list of inquiries to display in the modal
                  inquiries,

                  // function to call when the inquiry is displayed in the modal
                  inquiryViewedCallback: (inquiry: ConnectedInquiry): void => {
                    const { uuid, storefront } = inquiry;
                    setUserSettings(getInquiryUserSettingsKey(uuid)).catch(() => {
                      console.warn('Unable to mark inquiry as viewed'); // eslint-disable-line no-console
                    });

                    trackModalViewed({
                      ...defaultTrackingPayload,
                      inquiry_uuid: uuid,
                      name: storefront.vendorName,
                      vendor_category: storefront.taxonomyNode.key,
                    });
                  },

                  // function to call when the user submits the new inquiry status
                  sendModalClickedEvent: (
                    inquiry: ConnectedInquiry,
                    actionType: string,
                    valueSubmitted: string
                  ): void => {
                    const { uuid, storefront } = inquiry;
                    const payload = {
                      ...defaultTrackingPayload,
                      action_type: actionType,
                      inquiry_uuid: uuid,
                      name: storefront.vendorName,
                      vendor_category: storefront.taxonomyNode.key,
                      value_submitted: valueSubmitted,
                    };
                    trackModalClicked(payload);
                  },

                  // function to call when the modal is closed
                  closeCallback: (): void => {
                    dispatch(hideModal());
                  },
                },
                { size: 'sm', ariaLabel: 'Booking Status Reminder' }
              )
            );
          }
        })
        .catch(() => {
          console.warn('Unable to fetch inquiries'); // eslint-disable-line no-console
        });
    }
  }, [dispatch, user, weddingDate]);

  return null;
};

export default BookingStatusReminderContainer;
